import React from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import Box from '../Box/Box';
import { system } from 'styled-system';
import get from 'lodash/get';

export interface BaseInputProps
    extends React.PropsWithoutRef<JSX.IntrinsicElements['input']> {
    error?: boolean;
    isQuantity?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    endText?: React.ReactNode;
    index?: number;
    hint?: string;
    as?: React.ReactNode;
    increment?: () => void;
    decrement?: () => void;
}

const INPUT_PADDING = 8;
const ICON_OFFSET = 22;

const StyledInput = styled(Box)`
    ${system({ resize: true })}
    border: none;
    font-size: ${rem(14)};
    flex: 1;
    color: ${({ theme }) => theme.colors.primary['1800']};
    padding: ${rem(INPUT_PADDING)};
    height: ${rem(38)};
    background: transparent;
    text-align: ${({ isQuantity }) => (isQuantity ? 'center' : 'start')};

    ${({ startIcon, endIcon }) =>
        (startIcon || endIcon) &&
        css`
            padding-${startIcon ? 'left' : 'right'}: ${rem(
            INPUT_PADDING + ICON_OFFSET
        )};
        `}

    :focus {
        outline: none;
    }

    ${({ disabled }) =>
        disabled &&
        css`
            cursor: not-allowed;
        `}
`;

const Container = styled.div<BaseInputProps>`
    font-family: inherit;
    position: relative;
    width: 100%;
    display: flex;
    font-size: 16px;
    font-weight: 300;
    line-height: 1.38;
    transition: border 0.2s ease-out;
    border-radius: ${({ theme }) => theme.radii.sm};
    border: 1px solid transparent;
    overflow: hidden;

    ${({ bgColor, theme }) =>
        css`
            background: ${bgColor
                ? get(theme.colors, bgColor)
                : theme.colors.neutral[100]};
        `}

    :focus-within {
        border: 1px solid
            ${({ error, theme }) =>
                error ? theme.colors.error[700] : theme.colors.primary.main};
    }

    ${({ error, theme }) =>
        error &&
        css`
            border: 1px solid ${theme.colors.error[700]};
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
            cursor: not-allowed;
        `}
`;

const FloatingElementWrapper = styled(Box)`
    display: flex;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.neutral[1600]};
`;

const Input = React.forwardRef(
    (
        {
            error,
            startIcon,
            endIcon,
            endText,
            index,
            disabled,
            id,
            hint,
            bgColor,
            isQuantity,
            ...props
        }: BaseInputProps,
        ref
    ) => (
        <Box>
            <Container
                error={error}
                index={index}
                disabled={disabled}
                bgColor={bgColor}
                isQuantity={isQuantity}
            >
                {Boolean(startIcon) && (
                    <FloatingElementWrapper left={rem(INPUT_PADDING)}>
                        {startIcon}
                    </FloatingElementWrapper>
                )}
                {/* @ts-ignore */}
                <StyledInput
                    as="input"
                    ref={ref}
                    id={id}
                    disabled={disabled}
                    endIcon={endIcon}
                    startIcon={startIcon}
                    isQuantity={isQuantity}
                    {...props}
                />
                {Boolean(endIcon) && (
                    <FloatingElementWrapper right={rem(INPUT_PADDING)}>
                        {endIcon}
                    </FloatingElementWrapper>
                )}
                {Boolean(endText) && (
                    <FloatingElementWrapper right={rem(INPUT_PADDING)}>
                        {endText}
                    </FloatingElementWrapper>
                )}
            </Container>
        </Box>
    )
);

Input.displayName = 'Input';

export default Input;
